/* ──────────────────────────────────────────────────────────────
   verticalConfigs.js  -  config objects for every product vertical
   Adding a new vertical = adding one object here. Zero core changes.

   Sorting uses normalised `_n.*` fields (set by normalizers.js).
   Missing values sort to bottom (Infinity / -Infinity).
   ────────────────────────────────────────────────────────────── */

const fmtCurrency = (v) => {
  if (v == null || v === '' || v === 0) return '$0';
  return `$${Number(v).toLocaleString('en-CA')}`;
};
const fmtPercent = (v) => {
  if (v == null || v === '' || v === 0) return '0%';
  return `${Number(v)}%`;
};
const fmtMonths = (m) => {
  if (!m) return '-';
  if (m >= 12) return `${m / 12} año${m / 12 > 1 ? 's' : ''}`;
  return `${m} mes${m > 1 ? 'es' : ''}`;
};

// ────────────────────── BANK ACCOUNTS ──────────────────────
const bank_accounts = {
  key: 'bank_accounts',
  dataPath: 'bank_accounts.accounts',
  idField: 'account_id',
  nameField: 'name',
  institutionField: 'institution',
  layoutMode: 'ranked',

  pageTitle: 'Las mejores cuentas bancarias para newcomers en Canadá 2026',
  pageSubtitle: 'Comparamos cuentas chequing, savings y digitales para recién llegados latinos. Todas las cuentas con programas newcomer.',
  countLabel: 'cuentas',
  ctaLabel: 'Abrir cuenta',
  detailTitle: 'Detalles de la cuenta',

  personas: [
    { value: 'NEWCOMER', label: 'Newcomers' },
    { value: 'NO_FEE', label: 'Sin cuota' },
    { value: 'QUEBEC_FRIENDLY', label: 'Quebec' },
    { value: 'HIGH_INTEREST', label: 'Alto interés' },
    { value: 'FULL_SERVICE', label: 'Servicio completo' },
    { value: 'CASHBACK', label: 'Cashback' },
  ],

  sortOptions: [
    { value: 'finmaple_score', label: 'FinMaple Score' },
    { value: 'lowest_fee', label: 'Menor cuota mensual' },
    { value: 'highest_interest', label: 'Mayor interés' },
    { value: 'highest_bonus', label: 'Mayor bono bienvenida' },
  ],
  defaultSort: 'finmaple_score',
  sortFn: (items, key) => {
    const sorted = [...items];
    switch (key) {
      case 'finmaple_score':
        return sorted.sort((a, b) => (b._n?.finmaple_score ?? -Infinity) - (a._n?.finmaple_score ?? -Infinity));
      case 'lowest_fee':
        return sorted.sort((a, b) => (a._n?.monthly_fee_cad ?? Infinity) - (b._n?.monthly_fee_cad ?? Infinity));
      case 'highest_interest':
        return sorted.sort((a, b) => (b._n?.interest_rate_percent ?? -Infinity) - (a._n?.interest_rate_percent ?? -Infinity));
      case 'highest_bonus':
        return sorted.sort((a, b) => (b._n?.welcome_bonus_cad ?? -Infinity) - (a._n?.welcome_bonus_cad ?? -Infinity));
      default:
        return sorted;
    }
  },

  chips: (item) => [
    { label: item.fees?.monthly_fee === 0 ? 'Sin cuota' : `${fmtCurrency(item.fees?.monthly_fee)}/mes`, highlight: item.fees?.monthly_fee === 0 },
    { label: `${fmtPercent(item.interest?.rate_percent)} interés`, highlight: (item.interest?.rate_percent || 0) >= 2, show: (item.interest?.rate_percent || 0) > 0 },
    { label: 'Newcomers', highlight: true, show: item.newcomer_program?.eligible },
    { label: 'Digital', highlight: false, show: item.is_digital_only },
  ],

  comparisonRows: [
    { key: 'monthly_fee', label: 'Cuota mensual', render: (i) => fmtCurrency(i.fees?.monthly_fee) },
    { key: 'interest', label: 'Tasa de interés', render: (i) => fmtPercent(i.interest?.rate_percent) },
    { key: 'bonus', label: 'Bono bienvenida', render: (i) => i.welcome_bonus?.active ? fmtCurrency(i.welcome_bonus.value_cad) : '-' },
    { key: 'atm', label: 'Red ATM', render: (i) => i.features?.free_atm_network || '-' },
    { key: 'etransfer', label: 'E-transfers', render: (i) => i.features?.free_e_transfers ? 'Gratis' : 'Con costo' },
    { key: 'newcomer', label: 'Programa newcomer', render: (i) => i.newcomer_program?.eligible ? (i.newcomer_program.program_name || 'Sí') : 'No' },
    { key: 'deposit_insurance', label: 'Seguro de depósito', render: (i) => `${i.deposit_insurance?.provider || '-'} ${fmtCurrency(i.deposit_insurance?.coverage_amount)}` },
    { key: 'cashback', label: 'Cashback', render: (i) => (i.features?.cashback_percent || 0) > 0 ? `${i.features.cashback_percent}%` : '-' },
  ],

  detailSections: (item) => [
    {
      title: 'Cuotas y costos',
      rows: [
        { label: 'Cuota mensual', value: fmtCurrency(item.fees?.monthly_fee) },
        { label: 'Condición de exención', value: item.fees?.waiver_conditions || '-' },
        { label: 'Balance mínimo exención', value: item.fees?.min_balance_waiver ? fmtCurrency(item.fees.min_balance_waiver) : '-' },
        { label: 'Transacciones incluidas', value: (item.fees?.debit_txn_included >= 999) ? 'Ilimitadas' : String(item.fees?.debit_txn_included ?? '-') },
        { label: 'Costo transacción extra', value: item.fees?.extra_txn_fee ? fmtCurrency(item.fees.extra_txn_fee) : 'N/A' },
      ],
    },
    {
      title: 'Interés y recompensas',
      rows: [
        { label: 'Tasa de interés', value: fmtPercent(item.interest?.rate_percent) },
        { label: 'Condiciones de tasa', value: item.interest?.rate_conditions || '-' },
        { label: 'Cashback', value: (item.features?.cashback_percent || 0) > 0 ? `${item.features.cashback_percent}%` : '-' },
        { label: 'Programa de recompensas', value: item.features?.rewards_program || '-' },
      ],
    },
    {
      title: 'Características',
      rows: [
        { label: 'Red ATM', value: item.features?.free_atm_network || '-' },
        { label: 'E-transfers gratis', value: item.features?.free_e_transfers ? 'Sí' : 'No' },
        { label: 'Transferencias internacionales gratis', value: item.features?.international_transfers_free ? 'Sí' : 'No' },
        { label: 'Solo digital', value: item.is_digital_only ? 'Sí' : 'No' },
        { label: 'Seguro de depósito', value: `${item.deposit_insurance?.provider || '-'} - ${fmtCurrency(item.deposit_insurance?.coverage_amount)}` },
      ],
    },
    {
      title: 'Programa newcomer',
      rows: [
        { label: 'Elegible', value: item.newcomer_program?.eligible ? 'Sí' : 'No' },
        { label: 'Programa', value: item.newcomer_program?.program_name || '-' },
        { label: 'Meses sin cuota', value: item.newcomer_program?.fee_waiver_months ? fmtMonths(item.newcomer_program.fee_waiver_months) : '-' },
        { label: 'Ventana elegibilidad', value: item.newcomer_program?.window_years ? `${item.newcomer_program.window_years} años` : '-' },
      ],
    },
  ],

  recommendationLine: (item) => {
    if (item.newcomer_program?.eligible) return 'Recomendada para newcomers que buscan abrir su primera cuenta en Canadá.';
    if (item.fees?.monthly_fee === 0) return 'La mejor opción sin cuota mensual con el balance más fuerte de beneficios.';
    return 'La opción con mejor balance de costos, beneficios y accesibilidad para recién llegados.';
  },

  getApplyUrl: (item) => item.links?.apply_url || item.links?.official_url,
  getOfficialUrl: (item) => item.links?.official_url,
};

// ────────────────────── MORTGAGES ──────────────────────
const mortgages = {
  key: 'mortgages',
  dataPath: 'mortgages.programs',
  idField: 'mortgage_id',
  nameField: 'program_name',
  institutionField: 'lender',
  layoutMode: 'ranked',

  pageTitle: 'Programas hipotecarios para newcomers en Canadá 2026',
  pageSubtitle: 'Compara programas de hipoteca para recién llegados: CMHC, Sagen, bancos. Down payment desde 5%.',
  countLabel: 'programas',
  ctaLabel: 'Ver programa',
  detailTitle: 'Detalles del programa',

  personas: [
    { value: 'ALL_NEWCOMERS', label: 'Todos newcomers' },
    { value: 'PR', label: 'Residentes permanentes' },
    { value: 'WORK_PERMIT', label: 'Permiso de trabajo' },
  ],
  personaFilterFn: (item, active) => {
    if (active.length === 0) return true;
    return active.some((p) => item.target_audience === p || item.target_audience === 'ALL_NEWCOMERS');
  },

  sortOptions: [
    { value: 'finmaple_score', label: 'FinMaple Score' },
    { value: 'lowest_down', label: 'Menor down payment' },
    { value: 'longest_rate', label: 'Mayor rate guarantee' },
  ],
  defaultSort: 'finmaple_score',
  sortFn: (items, key) => {
    const sorted = [...items];
    switch (key) {
      case 'finmaple_score':
        return sorted.sort((a, b) => (b._n?.finmaple_score ?? -Infinity) - (a._n?.finmaple_score ?? -Infinity));
      case 'lowest_down':
        return sorted.sort((a, b) => (a._n?.down_payment_percent ?? Infinity) - (b._n?.down_payment_percent ?? Infinity));
      case 'longest_rate':
        return sorted.sort((a, b) => (b._n?.rate_guarantee_days ?? -Infinity) - (a._n?.rate_guarantee_days ?? -Infinity));
      default:
        return sorted;
    }
  },

  chips: (item) => [
    { label: `${item.down_payment?.minimum_percent ?? '?'}% min down`, highlight: (item.down_payment?.minimum_percent ?? 99) <= 5 },
    { label: item.rate_guarantee_days ? `${item.rate_guarantee_days}d rate hold` : 'Sin rate hold', highlight: (item.rate_guarantee_days ?? 0) >= 120, show: true },
    { label: item.target_audience?.replace(/_/g, ' ') || '-', highlight: false, show: true },
  ],

  comparisonRows: [
    { key: 'min_down', label: 'Min down payment', render: (i) => i.down_payment?.minimum_percent != null ? `${i.down_payment.minimum_percent}%` : '-' },
    { key: 'no_credit_down', label: 'Down sin crédito', render: (i) => i.down_payment?.minimum_percent_no_credit != null ? `${i.down_payment.minimum_percent_no_credit}%` : '-' },
    { key: 'rate_hold', label: 'Rate guarantee (días)', render: (i) => i.rate_guarantee_days || '-' },
    { key: 'cmhc', label: 'CMHC elegible', render: (i) => i.insurance?.cmhc_eligible ? '✓' : '✗' },
    { key: 'max_amort', label: 'Max amortización', render: (i) => i.amortization?.max_years != null ? `${i.amortization.max_years} años` : '-' },
    { key: 'ftb_30', label: '30 años FTB', render: (i) => i.amortization?.first_time_buyer_30yr ? '✓' : '✗' },
    { key: 'min_employment', label: 'Min empleo (meses)', render: (i) => i.credit_requirements?.min_employment_months ?? '-' },
  ],

  detailSections: (item) => [
    {
      title: 'Down payment',
      rows: [
        { label: 'Mínimo con crédito', value: item.down_payment?.minimum_percent != null ? `${item.down_payment.minimum_percent}%` : '-' },
        { label: 'Mínimo sin crédito', value: item.down_payment?.minimum_percent_no_credit != null ? `${item.down_payment.minimum_percent_no_credit}%` : '-' },
        { label: 'Notas', value: item.down_payment?.notes || '-' },
      ],
    },
    {
      title: 'Requisitos de crédito',
      rows: [
        { label: 'Crédito canadiense requerido', value: item.credit_requirements?.canadian_credit_required ? 'Sí' : 'No' },
        { label: 'Acepta crédito internacional', value: item.credit_requirements?.accepts_international_credit ? 'Sí' : 'No' },
        { label: 'Mínimo empleo', value: item.credit_requirements?.min_employment_months ? `${item.credit_requirements.min_employment_months} meses` : 'No requerido' },
      ],
    },
    {
      title: 'Amortización y seguro',
      rows: [
        { label: 'Max amortización', value: item.amortization?.max_years != null ? `${item.amortization.max_years} años` : '-' },
        { label: '30 años first-time buyer', value: item.amortization?.first_time_buyer_30yr ? 'Sí (regla dic 2024)' : 'No' },
        { label: 'Rate guarantee', value: item.rate_guarantee_days ? `${item.rate_guarantee_days} días` : '-' },
        { label: 'CMHC', value: item.insurance?.cmhc_eligible ? '✓' : '✗' },
        { label: 'Sagen', value: item.insurance?.sagen_eligible ? '✓' : '✗' },
        { label: 'Canada Guaranty', value: item.insurance?.canada_guaranty_eligible ? '✓' : '✗' },
      ],
    },
    {
      title: 'Características especiales',
      rows: (item.special_features || []).map((f, i) => ({ label: `#${i + 1}`, value: f })),
    },
    {
      title: 'Documentos requeridos',
      rows: (item.documents_required || []).map((d) => ({ label: '•', value: d })),
    },
  ],

  recommendationLine: (item) => {
    const audience = item.target_audience === 'PR' ? 'residentes permanentes' :
      item.target_audience === 'WORK_PERMIT' ? 'trabajadores con permiso de trabajo' : 'newcomers';
    return `El mejor programa hipotecario para ${audience} en Canadá.`;
  },

  getApplyUrl: (item) => item.links?.apply_url || item.links?.official_url,
  getOfficialUrl: (item) => item.links?.official_url,
};

// ────────────────────── REMITTANCES ──────────────────────
const remittances = {
  key: 'remittances',
  dataPath: 'remittances.providers',
  idField: 'provider_id',
  nameField: 'name',
  institutionField: 'type',
  layoutMode: 'ranked',

  pageTitle: 'Las mejores opciones para enviar remesas desde Canadá 2026',
  pageSubtitle: 'Compara fees, tasas de cambio y velocidad. Envía dinero a Colombia, México, Perú y más.',
  countLabel: 'proveedores',
  ctaLabel: 'Usar servicio',
  detailTitle: 'Detalles del proveedor',

  personas: [
    { value: 'FINTECH', label: 'Fintech' },
    { value: 'BANK', label: 'Banco' },
    { value: 'TRADITIONAL', label: 'Tradicional' },
  ],
  personaFilterFn: (item, active) => {
    if (active.length === 0) return true;
    return active.includes(item.type);
  },

  sortOptions: [
    { value: 'finmaple_score', label: 'FinMaple Score' },
    { value: 'lowest_fee', label: 'Menor fee' },
  ],
  defaultSort: 'finmaple_score',
  sortFn: (items, key) => {
    const sorted = [...items];
    if (key === 'finmaple_score') return sorted.sort((a, b) => (b._n?.finmaple_score ?? -Infinity) - (a._n?.finmaple_score ?? -Infinity));
    if (key === 'lowest_fee') return sorted.sort((a, b) => (a._n?.fee_cad ?? Infinity) - (b._n?.fee_cad ?? Infinity));
    return sorted;
  },

  chips: (item) => [
    { label: item.fee_structure || '-', highlight: item.fee_structure?.includes('$0'), show: true },
    { label: item.speed || '-', highlight: item.speed?.toLowerCase().includes('minut'), show: true },
    { label: item.exchange_rate?.includes('Mid-market') ? 'Tasa real' : 'Markup', highlight: item.exchange_rate?.includes('Mid-market'), show: true },
  ],

  comparisonRows: [
    { key: 'fee', label: 'Fee', render: (i) => i.fee_structure || '-' },
    { key: 'rate', label: 'Exchange rate', render: (i) => i.exchange_rate || '-' },
    { key: 'speed', label: 'Velocidad', render: (i) => i.speed || '-' },
    { key: 'max', label: 'Max transferencia', render: (i) => i.max_transfer || '-' },
    { key: 'app', label: 'App disponible', render: (i) => i.app_available ? 'Sí' : 'No' },
    { key: 'type', label: 'Tipo', render: (i) => i.type || '-' },
  ],

  detailSections: (item) => [
    {
      title: 'Costos y tasas',
      rows: [
        { label: 'Fee', value: item.fee_structure || '-' },
        { label: 'Tasa de cambio', value: item.exchange_rate || '-' },
        { label: 'CAD a COP', value: item.cad_to_cop_available ? 'Disponible' : 'No disponible' },
      ],
    },
    {
      title: 'Servicio',
      rows: [
        { label: 'Velocidad', value: item.speed || '-' },
        { label: 'Máximo por transferencia', value: item.max_transfer || '-' },
        { label: 'App disponible', value: item.app_available ? 'Sí' : 'No' },
        { label: 'Tipo de proveedor', value: item.type || '-' },
      ],
    },
  ],

  recommendationLine: (item) => {
    if (item.exchange_rate?.includes('Mid-market')) return 'La opción más transparente con la tasa de cambio real, sin markup oculto.';
    return 'La mejor combinación de velocidad, costo y confiabilidad para enviar remesas.';
  },

  getApplyUrl: () => null,
  getOfficialUrl: () => null,
};

// ────────────────────── REGISTERED ACCOUNTS ──────────────────────
const registered_accounts = {
  key: 'registered_accounts',
  dataPath: 'registered_accounts.accounts',
  idField: 'account_id',
  nameField: 'name',
  institutionField: 'type',
  layoutMode: 'cards',

  pageTitle: 'Cuentas registradas en Canadá: TFSA, RRSP, FHSA, RESP',
  pageSubtitle: 'Guía completa de cuentas con beneficios fiscales. Aprende cuál abrir primero como newcomer.',
  countLabel: 'cuentas',
  ctaLabel: 'Más información',
  detailTitle: 'Detalles de la cuenta',

  personas: [],

  sortOptions: [
    { value: 'finmaple_score', label: 'FinMaple Score' },
  ],
  defaultSort: 'finmaple_score',
  sortFn: (items, key) => {
    if (key === 'finmaple_score') return [...items].sort((a, b) => (b._n?.finmaple_score ?? -Infinity) - (a._n?.finmaple_score ?? -Infinity));
    return items;
  },

  chips: () => [],

  comparisonRows: [],

  detailSections: (item) => [
    {
      title: 'Información general',
      rows: [
        { label: 'Tipo', value: item.type || '-' },
        { label: 'Tratamiento fiscal', value: item.tax_treatment?.replace(/_/g, ' ') || '-' },
        { label: 'Límite contribución 2026', value: typeof item.contribution_limit_2026 === 'number' ? fmtCurrency(item.contribution_limit_2026) : (item.contribution_limit_2026 || '-') },
        { label: 'Límite acumulado', value: item.cumulative_limit_since_18_or_2009 || '-' },
      ],
    },
    {
      title: 'Elegibilidad',
      rows: [
        { label: 'Edad mínima', value: item.eligibility?.min_age != null ? String(item.eligibility.min_age) : '-' },
        { label: 'Residencia', value: item.eligibility?.residency || '-' },
        { label: 'Notas newcomer', value: item.eligibility?.newcomer_notes || '-' },
      ],
    },
    {
      title: 'Retiros',
      rows: [
        { label: 'Reglas de retiro', value: item.withdrawal_rules || '-' },
      ],
    },
  ],

  recommendationLine: () => 'La cuenta registrada más importante para tu primer año como newcomer en Canadá.',

  getApplyUrl: () => null,
  getOfficialUrl: () => null,
};

// ────────────────────── GICs ──────────────────────
const gics = {
  key: 'gics',
  dataPath: 'gics.top_rates_feb_2026',
  idField: 'provider',
  nameField: 'provider',
  institutionField: 'provider',
  layoutMode: 'table',

  pageTitle: 'Mejores tasas GIC en Canadá - Marzo 2026',
  pageSubtitle: 'Compara tasas de GIC (Guaranteed Investment Certificates) por plazo. Bancos online ofrecen ~1% más que los Big 5.',
  countLabel: 'proveedores',
  ctaLabel: 'Ver oferta',
  detailTitle: 'Detalles del GIC',

  personas: [],
  sortOptions: [],
  defaultSort: null,
  sortFn: (items) => items,
  chips: () => [],
  comparisonRows: [],
  detailSections: () => [],
  getApplyUrl: () => null,
  getOfficialUrl: () => null,

  // GIC-specific: term keys in the JSON
  termKeys: ['1_year', '2_year', '3_year', '4_year', '5_year'],
  termLabels: { '1_year': '1 año', '2_year': '2 años', '3_year': '3 años', '4_year': '4 años', '5_year': '5 años' },
};

// ────────────────────── INSURANCE ──────────────────────
const insurance = {
  key: 'insurance',
  dataPath: 'insurance.types',
  idField: 'insurance_id',
  nameField: 'name_es',
  institutionField: 'type_es',
  logoField: 'name',
  layoutMode: 'ranked',

  pageTitle: 'Seguros esenciales para newcomers en Canadá 2026',
  pageSubtitle: 'Salud pública, seguro privado, renter, auto y vida. Qué es obligatorio y qué es opcional.',
  countLabel: 'tipos de seguro',
  ctaLabel: 'Más información',
  detailTitle: 'Detalles del seguro',

  personas: [
    { value: 'mandatory', label: 'Obligatorio' },
    { value: 'optional', label: 'Opcional' },
  ],
  personaFilterFn: (item, active) => {
    if (active.length === 0) return true;
    if (active.includes('mandatory') && item.mandatory) return true;
    if (active.includes('optional') && !item.mandatory) return true;
    return false;
  },

  sortOptions: [
    { value: 'finmaple_score', label: 'FinMaple Score' },
    { value: 'mandatory_first', label: 'Obligatorios primero' },
  ],
  defaultSort: 'finmaple_score',
  sortFn: (items, key) => {
    const sorted = [...items];
    if (key === 'finmaple_score') return sorted.sort((a, b) => (b._n?.finmaple_score ?? -Infinity) - (a._n?.finmaple_score ?? -Infinity));
    if (key === 'mandatory_first') return sorted.sort((a, b) => (b._n?.mandatory ?? 0) - (a._n?.mandatory ?? 0));
    return sorted;
  },

  chips: (item) => [
    { label: item.mandatory ? 'Obligatorio' : 'Opcional', highlight: item.mandatory, show: true },
    { label: item.cost || '-', highlight: false, show: true },
    { label: item.type_es || item.type || '-', highlight: false, show: true },
  ],

  comparisonRows: [
    { key: 'mandatory', label: '¿Obligatorio?', render: (i) => i.mandatory ? 'Sí' : 'No' },
    { key: 'cost', label: 'Costo', render: (i) => i.cost || '-' },
    { key: 'covers', label: 'Cobertura', render: (i) => i.covers || '-' },
    { key: 'waiting', label: 'Período de espera', render: (i) => i.waiting_period || '-' },
    { key: 'province', label: 'Notas newcomer', render: (i) => i.newcomer_notes || '-' },
  ],

  detailSections: (item) => [
    {
      title: 'Cobertura',
      rows: [
        { label: 'Cubre', value: item.covers || '-' },
        { label: 'No cubre', value: item.does_not_cover || '-' },
        { label: 'Período de espera', value: item.waiting_period || '-' },
      ],
    },
    {
      title: 'Costos',
      rows: [
        { label: 'Costo', value: item.cost || '-' },
        { label: '¿Obligatorio?', value: item.mandatory ? 'Sí' : 'No' },
      ],
    },
    {
      title: 'Información newcomer',
      rows: [
        { label: 'Notas', value: item.newcomer_notes || '-' },
      ],
    },
    ...(item.best_providers ? [{
      title: 'Mejores proveedores',
      rows: item.best_providers.map((p) => ({ label: '•', value: p })),
    }] : []),
  ],

  recommendationLine: (item) => {
    if (item.mandatory) return 'Seguro esencial - obligatorio para todos los newcomers en Canadá.';
    return 'El seguro más recomendado para proteger tu patrimonio como newcomer.';
  },

  getApplyUrl: () => null,
  getOfficialUrl: () => null,
};

// ────────────────────── EXPORT ALL ──────────────────────
const verticalConfigs = {
  bank_accounts,
  mortgages,
  remittances,
  registered_accounts,
  gics,
  insurance,
};

export default verticalConfigs;
